import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Save, Loader2, Plus, X, CalendarDays, Clock, Moon, Globe2, AlertTriangle, Check,
} from 'lucide-react';
import { configService } from '../../services/configService';

// =============================================================================
// Manufacturing › Budget Setup › Overtime
//
// Captures the rules that turn a shift roster into paid hours:
//   · which days are the weekend (they move by country)
//   · the ordinary-hours threshold, daily or weekly
//   · a rate multiplier per day type, in bands (first N hours, then the rest)
//   · shift / night allowances, and what qualifies for them
//   · the public-holiday calendar
//
// SCOPE. One profile is the DEFAULT and applies to every site. Any site can be
// made an exception and pointed at its own profile — that is how a tenant runs
// ZA rules everywhere and DE rules at one plant.
//
// Stored as one JSON document in md_config.labour_rules. The same document is
// read by the SQL side (fn_labour_* / calc_labour_overtime), so nothing here
// needs mirroring into a table.
// =============================================================================

const DOWS = [
  { n: 1, label: 'Mon', code: 'MON' }, { n: 2, label: 'Tue', code: 'TUE' },
  { n: 3, label: 'Wed', code: 'WED' }, { n: 4, label: 'Thu', code: 'THU' },
  { n: 5, label: 'Fri', code: 'FRI' }, { n: 6, label: 'Sat', code: 'SAT' },
  { n: 7, label: 'Sun', code: 'SUN' },
];

// The day types a profile can price. Named ones win over the generic ones when
// a date matches both — a public holiday on a Saturday is priced as a holiday.
const DAY_TYPE_LABELS = {
  WEEKDAY: 'Weekday', WEEKEND: 'Weekend (either day)',
  MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday', THU: 'Thursday',
  FRI: 'Friday', SAT: 'Saturday', SUN: 'Sunday',
  PUBLIC_HOLIDAY: 'Public holiday', ANY: 'Any day',
};
const DAY_TYPE_ORDER = ['WEEKDAY', 'SAT', 'SUN', 'PUBLIC_HOLIDAY', 'WEEKEND',
  'MON', 'TUE', 'WED', 'THU', 'FRI', 'ANY'];

const BASES = [
  { value: 'PER_HOUR', label: 'Per hour in the window' },
  { value: 'PER_SHIFT', label: 'Per qualifying shift' },
  { value: 'PER_DAY', label: 'Per qualifying day' },
];

const blankProfile = (code, name) => ({
  code, name, country: '',
  weekend_days: [6, 7],
  ordinary: { basis: 'DAILY', hours_per_day: 9, hours_per_week: 45, max_overtime_hours_per_week: 10 },
  day_rules: [
    { day_type: 'WEEKDAY', counts_toward_ordinary: true, bands: [
      { from_hours: 0, multiplier: 1, category: 'ORDINARY' },
      { from_hours: 9, multiplier: 1.5, category: 'OT_1_5' }] },
    { day_type: 'SAT', counts_toward_ordinary: false, bands: [
      { from_hours: 0, multiplier: 1.5, category: 'SAT_1_5' }] },
    { day_type: 'SUN', counts_toward_ordinary: false, bands: [
      { from_hours: 0, multiplier: 2, category: 'SUN_2_0' }] },
    { day_type: 'PUBLIC_HOLIDAY', counts_toward_ordinary: false, bands: [
      { from_hours: 0, multiplier: 2, category: 'PH_2_0' }] },
  ],
  allowances: [
    { code: 'NIGHT', name: 'Night shift allowance', basis: 'PER_HOUR',
      window_start_hour: 18, window_end_hour: 6, min_overlap_hours: 1,
      day_types: ['ANY'], pct_of_hourly: 10, amount_per_unit: 0 },
  ],
  holidays: { dates: [], observance: [] },
});

const DEFAULT_DOC = {
  schema: 1,
  default_profile: 'DEFAULT',
  site_profiles: {},
  profiles: [blankProfile('DEFAULT', 'All sites')],
};

// ---- shared styles ----------------------------------------------------------
const card = 'rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800';
const input = 'rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 ' +
  'px-2.5 py-1.5 text-sm text-slate-800 dark:text-slate-100 focus:outline-none ' +
  'focus:ring-2 focus:ring-emerald-500/40';
const label = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const sectionTitle = 'flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100';
const ghostBtn = 'inline-flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 ' +
  'hover:bg-slate-50 dark:hover:bg-slate-700/50';

const numOr = (v, d) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? d : Number(v));

const OvertimeRules = () => {
  const [doc, setDoc] = useState(null);
  const [sites, setSites] = useState([]);
  const [activeCode, setActiveCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await configService.getLabourRules();
        if (!alive) return;
        const loaded = res?.data?.rules && Array.isArray(res.data.rules.profiles) && res.data.rules.profiles.length
          ? res.data.rules
          : DEFAULT_DOC;
        setDoc(loaded);
        setSites(res?.data?.sites || []);
        setActiveCode(loaded.default_profile || loaded.profiles[0].code);
      } catch (e) {
        if (alive) setError(e?.response?.data?.message || 'Could not load the overtime rules.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const profile = useMemo(
    () => doc?.profiles?.find((p) => p.code === activeCode) || doc?.profiles?.[0] || null,
    [doc, activeCode],
  );

  // Every mutation goes through here so `doc` is never edited in place.
  const patchProfile = useCallback((fn) => {
    setDoc((d) => ({
      ...d,
      profiles: d.profiles.map((p) => (p.code === activeCode ? fn({ ...p }) : p)),
    }));
    setSavedAt(0);
  }, [activeCode]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      await configService.saveLabourRules(doc);
      setSavedAt(Date.now());
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save the overtime rules.');
    } finally {
      setSaving(false);
    }
  };

  // --- profile management ----------------------------------------------------
  const addProfile = () => {
    const code = (window.prompt('Profile code (e.g. DE, AE, ZA-NIGHT)') || '').trim().toUpperCase();
    if (!code) return;
    if (doc.profiles.some((p) => p.code === code)) { setError(`Profile "${code}" already exists.`); return; }
    setDoc((d) => ({ ...d, profiles: [...d.profiles, blankProfile(code, code)] }));
    setActiveCode(code);
    setSavedAt(0);
  };

  const removeProfile = (code) => {
    if (code === doc.default_profile) { setError('The default profile applies to every site — make another profile the default first.'); return; }
    setDoc((d) => ({
      ...d,
      profiles: d.profiles.filter((p) => p.code !== code),
      // Any site pointing here falls back to the default.
      site_profiles: Object.fromEntries(
        Object.entries(d.site_profiles || {}).filter(([, c]) => c !== code)),
    }));
    setActiveCode(doc.default_profile);
    setSavedAt(0);
  };

  const makeDefault = (code) => { setDoc((d) => ({ ...d, default_profile: code })); setSavedAt(0); };

  // A site is either on the default (no entry) or an explicit exception.
  const setSiteProfile = (siteCode, profileCode) => {
    setDoc((d) => {
      const next = { ...(d.site_profiles || {}) };
      if (!profileCode) delete next[siteCode]; else next[siteCode] = profileCode;
      return { ...d, site_profiles: next };
    });
    setSavedAt(0);
  };

  // --- day rules -------------------------------------------------------------
  const patchRule = (dayType, fn) => patchProfile((p) => ({
    ...p, day_rules: p.day_rules.map((r) => (r.day_type === dayType ? fn({ ...r }) : r)),
  }));

  const addDayRule = (dayType) => patchProfile((p) => ({
    ...p,
    day_rules: [...p.day_rules, {
      day_type: dayType,
      counts_toward_ordinary: dayType === 'WEEKDAY' || dayType === 'ANY',
      bands: [{ from_hours: 0, multiplier: 1.5, category: `${dayType}_PREMIUM` }],
    }],
  }));

  const unusedDayTypes = useMemo(() => {
    const used = new Set((profile?.day_rules || []).map((r) => r.day_type));
    return DAY_TYPE_ORDER.filter((d) => !used.has(d));
  }, [profile]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <span className="inline-flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading overtime rules…
        </span>
      </div>
    );
  }
  if (!profile) return null;

  const ord = profile.ordinary || {};
  const weekly = String(ord.basis || 'DAILY').toUpperCase() === 'WEEKLY';

  return (
    <div className="h-full overflow-y-auto font-sans bg-slate-100 dark:bg-slate-900">
      <div className="max-w-5xl mx-auto px-6 py-10">

        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Overtime</h1>
            <p className="mt-2 text-base text-slate-500 dark:text-slate-400 max-w-2xl">
              The rules that turn a shift roster into paid hours. A profile applies to every site
              unless a site is named as an exception, so one tenant can run different countries’ rules side by side.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {savedAt > 0 && !saving && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                <Check className="w-3.5 h-3.5" /> Saved
              </span>
            )}
            <button
              type="button" onClick={save} disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500
                         disabled:opacity-60 px-4 py-2 text-sm font-semibold text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save rules
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-2 rounded-lg border border-red-300 dark:border-red-500/40
                          bg-red-50 dark:bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
          </div>
        )}

        {/* ---- profile picker ------------------------------------------- */}
        <div className={`${card} p-5 mb-6`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className={sectionTitle}><Globe2 className="w-4 h-4 text-slate-400" /> Rule profiles</h2>
            <button type="button" className={ghostBtn} onClick={addProfile}>
              <Plus className="w-3.5 h-3.5" /> Add profile
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {doc.profiles.map((p) => {
              const active = p.code === activeCode;
              const isDefault = p.code === doc.default_profile;
              return (
                <button
                  key={p.code} type="button" onClick={() => setActiveCode(p.code)}
                  className={`group inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${
                    active
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                      : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/40'
                  }`}
                >
                  <span className="font-medium">{p.code}</span>
                  <span className="text-xs text-slate-400">{p.name}</span>
                  {isDefault && (
                    <span className="rounded bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 text-[10px]
                                     font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                      All sites
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div>
              <span className={label}>Profile name</span>
              <input
                className={`${input} w-full`} value={profile.name || ''}
                onChange={(e) => patchProfile((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div>
              <span className={label}>Country (ISO, optional)</span>
              <input
                className={`${input} w-full`} value={profile.country || ''} placeholder="ZA"
                onChange={(e) => patchProfile((p) => ({ ...p, country: e.target.value.toUpperCase() }))}
              />
            </div>
            <div className="flex items-end gap-2">
              {profile.code !== doc.default_profile && (
                <>
                  <button type="button" className={ghostBtn} onClick={() => makeDefault(profile.code)}>
                    Apply to all sites
                  </button>
                  <button
                    type="button"
                    className={`${ghostBtn} text-red-600 dark:text-red-400`}
                    onClick={() => removeProfile(profile.code)}
                  >
                    <X className="w-3.5 h-3.5" /> Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ---- site exceptions ------------------------------------------- */}
        <div className={`${card} p-5 mb-6`}>
          <h2 className={`${sectionTitle} mb-1`}>Where these rules apply</h2>
          <p className="m-0 mb-4 text-xs text-slate-500 dark:text-slate-400">
            <span className="font-medium text-slate-700 dark:text-slate-200">{doc.default_profile}</span> applies to
            every site. Change a site below only when it is an exception — a plant in another country, or on a
            different agreement.
          </p>
          {sites.length === 0 ? (
            <p className="m-0 text-xs text-slate-400">No active sites.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {sites.map((s) => {
                const chosen = doc.site_profiles?.[s.site_code] || '';
                return (
                  <div key={s.site_code} className="flex items-center justify-between gap-3 rounded-lg
                                                    border border-slate-200 dark:border-slate-700 px-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-slate-800 dark:text-slate-100">{s.site_name}</div>
                      <div className="text-[11px] text-slate-400">{s.site_code}</div>
                    </div>
                    <select
                      className={`${input} shrink-0`} value={chosen}
                      onChange={(e) => setSiteProfile(s.site_code, e.target.value)}
                    >
                      <option value="">All sites ({doc.default_profile})</option>
                      {doc.profiles.filter((p) => p.code !== doc.default_profile).map((p) => (
                        <option key={p.code} value={p.code}>Exception · {p.code}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ---- ordinary hours -------------------------------------------- */}
        <div className={`${card} p-5 mb-6`}>
          <h2 className={`${sectionTitle} mb-1`}><Clock className="w-4 h-4 text-slate-400" /> Ordinary hours</h2>
          <p className="m-0 mb-4 text-xs text-slate-500 dark:text-slate-400">
            Where normal time stops and overtime starts. A daily threshold suits a shift roster; a weekly one
            suits jurisdictions that only count the week (the US 40-hour rule).
          </p>
          <div className="grid gap-3 sm:grid-cols-4">
            <div>
              <span className={label}>Threshold basis</span>
              <select
                className={`${input} w-full`} value={ord.basis || 'DAILY'}
                onChange={(e) => patchProfile((p) => ({ ...p, ordinary: { ...p.ordinary, basis: e.target.value } }))}
              >
                <option value="DAILY">Per day</option>
                <option value="WEEKLY">Per week</option>
              </select>
            </div>
            <div>
              <span className={label}>Hours per day</span>
              <input
                type="number" step="0.5" min="0" max="24"
                className={`${input} w-full ${weekly ? 'opacity-50' : ''}`}
                value={ord.hours_per_day ?? ''}
                onChange={(e) => patchProfile((p) => ({
                  ...p, ordinary: { ...p.ordinary, hours_per_day: numOr(e.target.value, 0) } }))}
              />
            </div>
            <div>
              <span className={label}>Hours per week</span>
              <input
                type="number" step="0.5" min="0" max="168"
                className={`${input} w-full ${weekly ? '' : 'opacity-50'}`}
                value={ord.hours_per_week ?? ''}
                onChange={(e) => patchProfile((p) => ({
                  ...p, ordinary: { ...p.ordinary, hours_per_week: numOr(e.target.value, 0) } }))}
              />
            </div>
            <div>
              <span className={label}>Max overtime / week</span>
              <input
                type="number" step="0.5" min="0" max="168"
                className={`${input} w-full`} value={ord.max_overtime_hours_per_week ?? ''}
                onChange={(e) => patchProfile((p) => ({
                  ...p, ordinary: { ...p.ordinary, max_overtime_hours_per_week: numOr(e.target.value, 0) } }))}
              />
            </div>
          </div>
          <p className="m-0 mt-2 text-[11px] text-slate-400">
            The cap is not enforced on the roster — a plan that breaches it still costs in full and raises a warning,
            so the budget shows what the roster actually asks for.
          </p>

          <div className="mt-5">
            <span className={label}>Weekend days</span>
            <div className="flex flex-wrap gap-1.5">
              {DOWS.map((d) => {
                const on = (profile.weekend_days || []).includes(d.n);
                return (
                  <button
                    key={d.n} type="button"
                    onClick={() => patchProfile((p) => {
                      const set = new Set(p.weekend_days || []);
                      if (set.has(d.n)) set.delete(d.n); else set.add(d.n);
                      return { ...p, weekend_days: [...set].sort((a, b) => a - b) };
                    })}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
                      on
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300'
                        : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
            <p className="m-0 mt-2 text-[11px] text-slate-400">
              Drives the generic “Weekend” and “Weekday” rates below. Fri/Sat in much of the Gulf, Sat/Sun elsewhere.
            </p>
          </div>
        </div>

        {/* ---- rates ------------------------------------------------------ */}
        <div className={`${card} p-5 mb-6`}>
          <div className="flex items-center justify-between mb-1">
            <h2 className={sectionTitle}>Rates</h2>
            {unusedDayTypes.length > 0 && (
              <select
                className={`${input} text-xs`} value=""
                onChange={(e) => e.target.value && addDayRule(e.target.value)}
              >
                <option value="">Add day type…</option>
                {unusedDayTypes.map((d) => <option key={d} value={d}>{DAY_TYPE_LABELS[d]}</option>)}
              </select>
            )}
          </div>
          <p className="m-0 mb-4 text-xs text-slate-500 dark:text-slate-400">
            A multiplier on the person’s ordinary hourly rate. Bands run from the hour shown until the next band
            starts — {weekly ? 'counted against the running week' : 'counted against the day'}. Where a date matches
            more than one row, the most specific wins: public holiday, then a named weekday, then weekend/weekday.
          </p>

          <div className="space-y-3">
            {[...(profile.day_rules || [])]
              .sort((a, b) => DAY_TYPE_ORDER.indexOf(a.day_type) - DAY_TYPE_ORDER.indexOf(b.day_type))
              .map((rule) => (
                <div key={rule.day_type} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {DAY_TYPE_LABELS[rule.day_type] || rule.day_type}
                    </span>
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                        <input
                          type="checkbox" checked={!!rule.counts_toward_ordinary}
                          onChange={(e) => patchRule(rule.day_type, (r) => ({ ...r, counts_toward_ordinary: e.target.checked }))}
                        />
                        Counts toward the ordinary threshold
                      </label>
                      <button
                        type="button" className="text-slate-400 hover:text-red-500"
                        onClick={() => patchProfile((p) => ({
                          ...p, day_rules: p.day_rules.filter((r) => r.day_type !== rule.day_type) }))}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {rule.bands.map((b, i) => (
                      <div key={i} className="flex flex-wrap items-end gap-2">
                        <div className="w-28">
                          <span className={label}>From hour</span>
                          <input
                            type="number" step="0.5" min="0" max="24" className={`${input} w-full`}
                            value={b.from_hours ?? 0}
                            onChange={(e) => patchRule(rule.day_type, (r) => ({
                              ...r, bands: r.bands.map((x, j) => (j === i ? { ...x, from_hours: numOr(e.target.value, 0) } : x)) }))}
                          />
                        </div>
                        <div className="w-28">
                          <span className={label}>Multiplier</span>
                          <input
                            type="number" step="0.05" min="0" max="10" className={`${input} w-full`}
                            value={b.multiplier ?? 1}
                            onChange={(e) => patchRule(rule.day_type, (r) => ({
                              ...r, bands: r.bands.map((x, j) => (j === i ? { ...x, multiplier: numOr(e.target.value, 1) } : x)) }))}
                          />
                        </div>
                        <div className="flex-1 min-w-[10rem]">
                          <span className={label}>Pay category</span>
                          <input
                            className={`${input} w-full`} value={b.category || ''}
                            onChange={(e) => patchRule(rule.day_type, (r) => ({
                              ...r, bands: r.bands.map((x, j) => (j === i ? { ...x, category: e.target.value.toUpperCase() } : x)) }))}
                          />
                        </div>
                        {rule.bands.length > 1 && (
                          <button
                            type="button" className="mb-1.5 text-slate-400 hover:text-red-500"
                            onClick={() => patchRule(rule.day_type, (r) => ({
                              ...r, bands: r.bands.filter((_, j) => j !== i) }))}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button" className={`${ghostBtn} mt-2`}
                    onClick={() => patchRule(rule.day_type, (r) => ({
                      ...r,
                      bands: [...r.bands, {
                        from_hours: Math.max(...r.bands.map((x) => numOr(x.from_hours, 0))) + 1,
                        multiplier: 2, category: 'OT_2_0',
                      }] }))}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add band
                  </button>
                </div>
              ))}
          </div>
        </div>

        {/* ---- allowances -------------------------------------------------- */}
        <div className={`${card} p-5 mb-6`}>
          <div className="flex items-center justify-between mb-1">
            <h2 className={sectionTitle}><Moon className="w-4 h-4 text-slate-400" /> Shift &amp; night allowances</h2>
            <button
              type="button" className={ghostBtn}
              onClick={() => patchProfile((p) => ({
                ...p,
                allowances: [...(p.allowances || []), {
                  code: `ALLOW${(p.allowances?.length || 0) + 1}`, name: 'New allowance',
                  basis: 'PER_SHIFT', min_overlap_hours: 0, day_types: ['ANY'],
                  pct_of_hourly: 0, amount_per_unit: 0,
                }] }))}
            >
              <Plus className="w-3.5 h-3.5" /> Add allowance
            </button>
          </div>
          <p className="m-0 mb-4 text-xs text-slate-500 dark:text-slate-400">
            Money earned for <em>when</em> a shift is worked rather than how long. A window that starts later than it
            ends wraps midnight — 18:00 to 06:00 is a night. Leave both blank to pay on every shift.
            Set a percentage of the person’s own hourly rate, or a flat amount per unit.
          </p>

          <div className="space-y-3">
            {(profile.allowances || []).map((a, i) => {
              const patchAllow = (fn) => patchProfile((p) => ({
                ...p, allowances: p.allowances.map((x, j) => (j === i ? fn({ ...x }) : x)) }));
              return (
                <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                  <div className="grid gap-2 sm:grid-cols-4">
                    <div>
                      <span className={label}>Code</span>
                      <input
                        className={`${input} w-full`} value={a.code || ''}
                        onChange={(e) => patchAllow((x) => ({ ...x, code: e.target.value.toUpperCase() }))}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <span className={label}>Name</span>
                      <input
                        className={`${input} w-full`} value={a.name || ''}
                        onChange={(e) => patchAllow((x) => ({ ...x, name: e.target.value }))}
                      />
                    </div>
                    <div className="flex items-end justify-end">
                      <button
                        type="button" className="mb-1.5 text-slate-400 hover:text-red-500"
                        onClick={() => patchProfile((p) => ({
                          ...p, allowances: p.allowances.filter((_, j) => j !== i) }))}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-2 grid gap-2 sm:grid-cols-6">
                    <div className="sm:col-span-2">
                      <span className={label}>Paid</span>
                      <select
                        className={`${input} w-full`} value={a.basis || 'PER_SHIFT'}
                        onChange={(e) => patchAllow((x) => ({ ...x, basis: e.target.value }))}
                      >
                        {BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <span className={label}>Window from</span>
                      <input
                        type="number" step="1" min="0" max="24" placeholder="—"
                        className={`${input} w-full`} value={a.window_start_hour ?? ''}
                        onChange={(e) => patchAllow((x) => ({
                          ...x, window_start_hour: e.target.value === '' ? null : numOr(e.target.value, 0) }))}
                      />
                    </div>
                    <div>
                      <span className={label}>Window to</span>
                      <input
                        type="number" step="1" min="0" max="24" placeholder="—"
                        className={`${input} w-full`} value={a.window_end_hour ?? ''}
                        onChange={(e) => patchAllow((x) => ({
                          ...x, window_end_hour: e.target.value === '' ? null : numOr(e.target.value, 0) }))}
                      />
                    </div>
                    <div>
                      <span className={label}>Min hrs in window</span>
                      <input
                        type="number" step="0.5" min="0" max="24"
                        className={`${input} w-full`} value={a.min_overlap_hours ?? 0}
                        onChange={(e) => patchAllow((x) => ({ ...x, min_overlap_hours: numOr(e.target.value, 0) }))}
                      />
                    </div>
                    <div>
                      <span className={label}>% of hourly</span>
                      <input
                        type="number" step="0.5" min="0"
                        className={`${input} w-full`} value={a.pct_of_hourly ?? 0}
                        onChange={(e) => patchAllow((x) => ({ ...x, pct_of_hourly: numOr(e.target.value, 0) }))}
                      />
                    </div>
                  </div>

                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <div>
                      <span className={label}>Flat amount per unit</span>
                      <input
                        type="number" step="0.01" min="0"
                        className={`${input} w-full`} value={a.amount_per_unit ?? 0}
                        onChange={(e) => patchAllow((x) => ({ ...x, amount_per_unit: numOr(e.target.value, 0) }))}
                      />
                    </div>
                    <div>
                      <span className={label}>Only on these days</span>
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {['ANY', 'WEEKDAY', 'SAT', 'SUN', 'PUBLIC_HOLIDAY'].map((d) => {
                          const on = (a.day_types || ['ANY']).includes(d);
                          return (
                            <button
                              key={d} type="button"
                              onClick={() => patchAllow((x) => {
                                const set = new Set(x.day_types || ['ANY']);
                                if (d === 'ANY') return { ...x, day_types: ['ANY'] };
                                set.delete('ANY');
                                if (set.has(d)) set.delete(d); else set.add(d);
                                return { ...x, day_types: set.size ? [...set] : ['ANY'] };
                              })}
                              className={`rounded border px-2 py-1 text-[11px] ${
                                on
                                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
                                  : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'
                              }`}
                            >
                              {DAY_TYPE_LABELS[d]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ---- public holidays --------------------------------------------- */}
        <div className={`${card} p-5 mb-10`}>
          <div className="flex items-center justify-between mb-1">
            <h2 className={sectionTitle}><CalendarDays className="w-4 h-4 text-slate-400" /> Public holidays</h2>
            <button
              type="button" className={ghostBtn}
              onClick={() => patchProfile((p) => ({
                ...p,
                holidays: {
                  ...(p.holidays || {}),
                  dates: [...(p.holidays?.dates || []), { month: 1, day: 1, name: 'New holiday' }],
                } }))}
            >
              <Plus className="w-3.5 h-3.5" /> Add holiday
            </button>
          </div>
          <p className="m-0 mb-4 text-xs text-slate-500 dark:text-slate-400">
            A holiday on a fixed day each year is captured as month + day and repeats automatically. Moveable
            ones — Easter, Eid, Lunar New Year — are captured as a real date, one row per year.
          </p>

          <div className="space-y-2">
            {(profile.holidays?.dates || []).length === 0 && (
              <p className="m-0 text-xs text-slate-400">
                No holidays captured — no public-holiday premium will be calculated.
              </p>
            )}
            {(profile.holidays?.dates || []).map((h, i) => {
              const patchHol = (fn) => patchProfile((p) => ({
                ...p,
                holidays: { ...p.holidays, dates: p.holidays.dates.map((x, j) => (j === i ? fn({ ...x }) : x)) } }));
              const recurring = !h.date;
              return (
                <div key={i} className="flex flex-wrap items-end gap-2 rounded-lg border
                                        border-slate-200 dark:border-slate-700 p-2.5">
                  <div className="flex-1 min-w-[12rem]">
                    <span className={label}>Name</span>
                    <input
                      className={`${input} w-full`} value={h.name || ''}
                      onChange={(e) => patchHol((x) => ({ ...x, name: e.target.value }))}
                    />
                  </div>
                  <div className="w-40">
                    <span className={label}>Repeats</span>
                    <select
                      className={`${input} w-full`} value={recurring ? 'YEARLY' : 'ONCE'}
                      onChange={(e) => patchHol((x) => (e.target.value === 'YEARLY'
                        ? { name: x.name, month: 1, day: 1 }
                        : { name: x.name, date: new Date().toISOString().slice(0, 10) }))}
                    >
                      <option value="YEARLY">Every year</option>
                      <option value="ONCE">A specific date</option>
                    </select>
                  </div>
                  {recurring ? (
                    <>
                      <div className="w-24">
                        <span className={label}>Month</span>
                        <input
                          type="number" min="1" max="12" className={`${input} w-full`} value={h.month ?? 1}
                          onChange={(e) => patchHol((x) => ({ ...x, month: numOr(e.target.value, 1) }))}
                        />
                      </div>
                      <div className="w-24">
                        <span className={label}>Day</span>
                        <input
                          type="number" min="1" max="28" className={`${input} w-full`} value={h.day ?? 1}
                          onChange={(e) => patchHol((x) => ({ ...x, day: numOr(e.target.value, 1) }))}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="w-44">
                      <span className={label}>Date</span>
                      <input
                        type="date" className={`${input} w-full`} value={h.date || ''}
                        onChange={(e) => patchHol((x) => ({ ...x, date: e.target.value }))}
                      />
                    </div>
                  )}
                  <button
                    type="button" className="mb-1.5 text-slate-400 hover:text-red-500"
                    onClick={() => patchProfile((p) => ({
                      ...p, holidays: { ...p.holidays, dates: p.holidays.dates.filter((_, j) => j !== i) } }))}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Observance — a holiday landing on a given weekday is moved or doubled. */}
          <div className="mt-5 border-t border-slate-200 dark:border-slate-700 pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-slate-800 dark:text-slate-100">Observance</span>
              <button
                type="button" className={ghostBtn}
                onClick={() => patchProfile((p) => ({
                  ...p,
                  holidays: {
                    ...(p.holidays || {}),
                    observance: [...(p.holidays?.observance || []),
                      { when_dow: [7], shift_days: 1, keep_original: true }],
                  } }))}
              >
                <Plus className="w-3.5 h-3.5" /> Add rule
              </button>
            </div>
            <p className="m-0 mb-3 text-xs text-slate-500 dark:text-slate-400">
              What happens when a holiday lands on a weekend. South Africa <em>adds</em> the Monday when a holiday
              falls on a Sunday; the US and UK <em>move</em> it instead.
            </p>
            <div className="space-y-2">
              {(profile.holidays?.observance || []).map((o, i) => {
                const patchObs = (fn) => patchProfile((p) => ({
                  ...p,
                  holidays: { ...p.holidays, observance: p.holidays.observance.map((x, j) => (j === i ? fn({ ...x }) : x)) } }));
                return (
                  <div key={i} className="flex flex-wrap items-end gap-2 rounded-lg border
                                          border-slate-200 dark:border-slate-700 p-2.5">
                    <div>
                      <span className={label}>When it falls on</span>
                      <div className="flex gap-1.5 pt-1">
                        {DOWS.map((d) => {
                          const on = (o.when_dow || []).includes(d.n);
                          return (
                            <button
                              key={d.n} type="button"
                              onClick={() => patchObs((x) => {
                                const set = new Set(x.when_dow || []);
                                if (set.has(d.n)) set.delete(d.n); else set.add(d.n);
                                return { ...x, when_dow: [...set].sort((a, b) => a - b) };
                              })}
                              className={`rounded border px-2 py-1 text-[11px] ${
                                on
                                  ? 'border-amber-500 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300'
                                  : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'
                              }`}
                            >
                              {d.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="w-32">
                      <span className={label}>Shift by (days)</span>
                      <input
                        type="number" min="-7" max="7" className={`${input} w-full`} value={o.shift_days ?? 1}
                        onChange={(e) => patchObs((x) => ({ ...x, shift_days: numOr(e.target.value, 0) }))}
                      />
                    </div>
                    <label className="mb-2 inline-flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                      <input
                        type="checkbox" checked={!!o.keep_original}
                        onChange={(e) => patchObs((x) => ({ ...x, keep_original: e.target.checked }))}
                      />
                      Keep the original day too
                    </label>
                    <button
                      type="button" className="mb-1.5 ml-auto text-slate-400 hover:text-red-500"
                      onClick={() => patchProfile((p) => ({
                        ...p,
                        holidays: { ...p.holidays, observance: p.holidays.observance.filter((_, j) => j !== i) } }))}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default OvertimeRules;
